'use client'
import { useEffect, useRef, useState } from 'react'
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Text,
  Select,
  Box,
  Checkbox,
  Link,
  FormErrorMessage,
  InputGroup,
  InputLeftAddon,
  List,
  Spinner,
  ListItem,
} from '@chakra-ui/react'
import { Button } from '../ui/button'
import { useRouter, useSearchParams } from 'next/navigation'
import NextLink from 'next/link'
import { useForm } from 'react-hook-form'
import { useLoginMutation } from '@/src/mutations/auth/login-mutation'
import { useSignUpMutation } from '@/src/mutations/auth/signup-mutation'
import { toast } from 'sonner'
import { toTitleCase } from '@/src/lib/utils'
import { useIndustryQuery } from '@/src/queries/auth/industry-query'
import { useCountriesQuery } from '@/src/queries/auth/country-query'
import PasswordInput from './password-input'
import { ENABLED_COUNTRIES } from '@/lib/enabled_countries'
import { GetTurnstileToken } from '@/src/lib/cloudflareTurnstile'
import { useLocationQuery } from '@/src/queries/guest/location-query'
import { useSuggestChannelNamesMutation } from '@/src/mutations/auth/channel-name'
import { debounce } from 'lodash'
import { usePostHogEvents, EVENTS } from '@/lib/posthog-events'

interface BusinessAuthFormData {
  businessName?: string
  email: string
  publicChannelName?: string
  industry?: string
  subcategory?: string
  password: string
  confirmPassword?: string
  country?: string
  phone?: string
}

const BusinessAuthForm = ({
  formType,
  isTv,
}: {
  formType: 'login' | 'signup'
  isTv?: boolean
}) => {
  const { trackEvent } = usePostHogEvents()
  const {
    handleSubmit,
    register,
    watch,
    setError,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = useForm<BusinessAuthFormData>()
  const [countryCode, setCountryCode] = useState('91')
  const [isFetchingSignupToken, setIsFetchingSignupToken] = useState(false)
  const loginMutation = useLoginMutation()
  const countries = useCountriesQuery()
  const { data: locationData } = useLocationQuery()
  const searchParams = useSearchParams()
  const queryCode = searchParams.get('code')
  const signupMutation = useSignUpMutation()

  const inputStyles = {
    size: {
      base: 'md',
    },
    rounded: 'md',
    border: '1px solid #E5E5E5',
    px: {
      base: '3',
      md: '4',
    },
    fontSize: {
      base: 'sm',
      md: 'md',
    },
  }

  const industries = useIndustryQuery()

  const labelStyles = {
    fontSize: {
      base: 'sm',
      md: 'md',
    },
    mt: {
      base: '1',
      md: '1',
    },
    fontWeight: '500',
    marginBottom: '1px',
    color: '#525252',
  }

  const selectedCountry = watch('country')
  const selectedIndustry = watch('industry')
  const isMediaEntertainmentTelecom =
    industries.data
      ?.find((industry) => industry.id === selectedIndustry)
      ?.catagory.trim()
      .toLowerCase() === 'media, entertainment & telecom'
  const countryCodeByCountry = countries.data?.find(
    (country) => country.id === selectedCountry
  )?.dialCode
  const dialCodeLength = countryCode.replace(/\D/g, '').length
  const phoneNumberLength = Math.min(10, 12 - dialCodeLength)

  useEffect(() => {
    if (selectedCountry) {
      setCountryCode(countryCodeByCountry || '')
    }
  }, [countryCodeByCountry, selectedCountry])

  useEffect(() => {
    if (getValues('phone')) {
      void trigger('phone')
    }
  }, [countryCode, getValues, trigger])

  useEffect(() => {
    if (!isMediaEntertainmentTelecom) {
      setValue('subcategory', undefined)
    }
  }, [isMediaEntertainmentTelecom, setValue])

  const onSubmit = async (data: BusinessAuthFormData) => {
    if (formType === 'login') {
      loginMutation.mutate(
        {
          email: data.email,
          password: data.password,
          type: 'business',
          inputType: 'email',
        },
        {
          onSuccess: (user) => {
            toast.success('Login successful!')
            trackEvent(EVENTS.USER_LOGGED_IN, {
              account_type: 'business',
              provider: 'email',
              is_tv: !!isTv,
            })
          },
          onError: (error) => {
            toast.error(error.message)
            setError('root', {
              type: 'manual',
              message: error.message,
            })
          },
        }
      )
    } else {
      if (data.password !== data.confirmPassword) {
        setError('confirmPassword', {
          type: 'manual',
          message: 'Passwords do not match',
        })
        return
      }
      if (
        data.phone &&
        !new RegExp(`^[0-9]{${phoneNumberLength}}$`).test(data.phone)
      ) {
        setError('phone', {
          type: 'manual',
          message: `Phone number must be ${phoneNumberLength} digits`,
        })
        return
      }
      setIsFetchingSignupToken(true)
      const cfToken = await GetTurnstileToken('cft-UserSignUp')
      // console.log('Business signup Turnstile token:', cfToken)

      if (!cfToken) {
        setIsFetchingSignupToken(false)
        setError('root', {
          type: 'manual',
          message: 'Unable to generate Turnstile token.',
        })
        return
      }

      signupMutation.mutate(
        {
          email: data.email,
          password: data.password,
          name: data.businessName!,
          countryCode: data.phone ? countryCode : countryCodeByCountry || '',
          phone: data.phone || '',
          publicChannelName: data.publicChannelName,
          industryId: data.industry,
          subCategoryID: data.subcategory,
          type: 'business',
          countryId: data.country || '',
          orgName: data.businessName,
          cfToken: cfToken || '',
        },
        {
          onSuccess: async () => {
            toast.success('Signup successful!')
            trackEvent(EVENTS.USER_SIGNED_UP, {
              account_type: 'business',
              provider: 'email',
              is_tv: !!isTv,
            })
          },
          onError: (error) => {
            toast.error(error.message)
            setError('root', {
              type: 'manual',
              message: error.message,
            })
          },
          onSettled: () => {
            setIsFetchingSignupToken(false)
          },
        }
      )
    }
  }

  const router = useRouter()

  const handlePasswordFlow = () => {
    router.push('/auth/password')
  }
  useEffect(() => {
    if (locationData?.countryCode === 'IN') {
      setValue('country', '99')
    } else {
      setValue('country', '226')
    }
  }, [locationData])

  const [suggestions, setSuggestions] = useState<string[]>([])
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const { mutate: suggestChannelNames, isPending } =
    useSuggestChannelNamesMutation()

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setDropdownVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleInputChange = debounce((value: string) => {
    if (value.trim()) {
      suggestChannelNames(
        { channelName: value },
        {
          onSuccess: (data: string[]) => {
            setSuggestions(data)
            setDropdownVisible(true)
          },
          onError: (error: any) => {
            setError('publicChannelName', {
              type: 'manual',
              message: error.message,
            })
            setSuggestions([])
            setDropdownVisible(false)
          },
        }
      )
    } else {
      setSuggestions([])
      setDropdownVisible(false)
    }
  }, 300)

  const handleKeyDown = (e: any) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prevIndex) =>
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
      )
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex])
      }
    } else if (e.key === 'Tab') {
    }
  }

  useEffect(() => {
    if (isDropdownVisible) {
      document.addEventListener('keydown', handleKeyDown)
    } else {
      document.removeEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [highlightedIndex, isDropdownVisible])

  const handleSelectSuggestion = (suggestion: string) => {
    setValue('publicChannelName', suggestion)
    setDropdownVisible(false)
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <VStack
          alignItems={{ base: 'flex-start', md: 'flex-start' }}
          className="w-full px-0"
        >
          {formType === 'signup' && (
            <FormControl isRequired isInvalid={!!errors.businessName}>
              <FormLabel htmlFor="businessName" {...labelStyles}>
                Enter business name
              </FormLabel>
              <Input
                {...inputStyles}
                {...register('businessName', {
                  required: 'This field is required',
                })}
              />
              <FormErrorMessage>
                <>{errors.businessName && errors.businessName.message}</>
              </FormErrorMessage>
            </FormControl>
          )}
          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel htmlFor="email" {...labelStyles}>
              {formType === 'login'
                ? 'Email Address'
                : 'Please enter your work e-mail ID or user ID'}
            </FormLabel>
            <Input
              type="email"
              {...inputStyles}
              {...register('email', {
                required: 'This field is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: 'Invalid email address',
                },
              })}
            />
            <FormErrorMessage>
              <>{errors.email && errors.email.message}</>
            </FormErrorMessage>
          </FormControl>
          {formType === 'signup' && (
            <>
              <FormControl isRequired isInvalid={!!errors.publicChannelName}>
                <FormLabel htmlFor="publicChannelName" {...labelStyles}>
                  Public Channel Name
                </FormLabel>
                <Input
                  id="publicChannelName"
                  maxLength={50}
                  {...inputStyles}
                  {...register('publicChannelName', {
                    required: 'This field is required',
                    pattern: {
                      value: /^[a-zA-Z0-9\s]*$/,
                      message: 'Only letters and numbers are allowed',
                    },
                    onChange: (e) => handleInputChange(e.target.value),
                  })}
                  onKeyDown={handleKeyDown}
                />
                <FormErrorMessage>
                  {errors.publicChannelName && errors.publicChannelName.message}
                </FormErrorMessage>

                {isDropdownVisible && suggestions.length > 0 && (
                  <Box
                    mt={2}
                    border="1px solid #ccc"
                    borderRadius="md"
                    ref={suggestionsRef}
                    position="absolute"
                    zIndex={10}
                    backgroundColor="white"
                    width="100%"
                  >
                    <List borderRadius="md">
                      {isPending ? (
                        <Spinner size="sm" />
                      ) : (
                        suggestions.map((suggestion, index) => (
                          <ListItem
                            key={suggestion}
                            onClick={() => handleSelectSuggestion(suggestion)}
                            cursor="pointer"
                            fontSize={14}
                            borderRadius="md"
                            color={'#7A7A7A'}
                            fontWeight={500}
                            p={2}
                            _hover={{ backgroundColor: '#F4F4F4' }}
                            backgroundColor={
                              highlightedIndex === index ? '#F4F4F4' : 'white'
                            }
                          >
                            {suggestion}
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Box>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.industry}>
                <FormLabel htmlFor="industry" {...labelStyles}>
                  Industry
                </FormLabel>
                <Select
                  fontSize={{
                    base: 'sm',
                    md: 'md',
                  }}
                  size={{
                    base: 'md',
                  }}
                  placeholder="Select an industry"
                  border="1px solid #E5E5E5"
                  {...register('industry', {
                    required: 'This field is required',
                  })}
                >
                  {industries.data?.map((industry, i) => (
                    <option key={i} value={industry.id}>
                      {industry.catagory}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>
                  <>{errors.industry && errors.industry.message}</>
                </FormErrorMessage>
              </FormControl>
              {isMediaEntertainmentTelecom && (
                <FormControl isRequired isInvalid={!!errors.subcategory}>
                  <FormLabel htmlFor="subcategory" {...labelStyles}>
                    Sub-Category
                  </FormLabel>
                  <Select
                    fontSize={{
                      base: 'sm',
                      md: 'md',
                    }}
                    size={{
                      base: 'md',
                    }}
                    placeholder="Select a sub-category"
                    border="1px solid #E5E5E5"
                    {...register('subcategory', {
                      required: 'This field is required',
                    })}
                  >
                    <option value="1">Podcast</option>
                    <option value="2">Other</option>
                  </Select>
                  <FormErrorMessage>
                    <>{errors.subcategory && errors.subcategory.message}</>
                  </FormErrorMessage>
                </FormControl>
              )}
            </>
          )}
          {formType == 'signup' && (
            <>
              <FormControl isRequired isInvalid={!!errors.country}>
                <FormLabel htmlFor="country" {...labelStyles}>
                  Country
                </FormLabel>
                <Select
                  {...register('country', {
                    required: 'This field is required',
                  })}
                  fontSize={{
                    base: 'sm',
                    md: 'md',
                  }}
                  size={{
                    base: 'md',
                  }}
                  placeholder="Select Country"
                  border="1px solid #E5E5E5"
                  value={getValues('country')}
                >
                  {countries.data
                    ?.filter((country) =>
                      ENABLED_COUNTRIES.includes(country.name.toLowerCase())
                    )
                    .map((country) => (
                      <option value={country.id} key={country.id}>
                        {toTitleCase(country.name)}
                      </option>
                    ))}
                </Select>
                <FormErrorMessage>
                  <>{errors.country && errors.country.message}</>
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.phone}>
                <FormLabel htmlFor="phone" {...labelStyles}>
                  Phone Number
                </FormLabel>
                <InputGroup>
                  <InputLeftAddon
                    border={'1px solid #E5E5E5'}
                    minW={'60px'}
                    fontSize={{
                      base: 'sm',
                      md: 'md',
                    }}
                    px={0}
                  >
                    <Select
                      fontSize={{
                        base: 'sm',
                        md: 'md',
                      }}
                      id="countryCode"
                      name="countryCode"
                      size={{
                        base: 'md',
                      }}
                      border={'none'}
                      _focusVisible={{
                        border: 'none',
                        boxShadow: 'none',
                      }}
                      value={countryCode}
                      onChange={(e) => {
                        setCountryCode(e.target.value)
                      }}
                      isDisabled
                    >
                      {countries.data
                        ?.filter((country) =>
                          ENABLED_COUNTRIES.includes(country.name.toLowerCase())
                        )
                        .map((country) => (
                          <option
                            value={country.dialCode}
                            key={country.dialCode}
                          >
                            +{country.dialCode}
                          </option>
                        ))}
                    </Select>
                  </InputLeftAddon>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    maxLength={phoneNumberLength}
                    {...inputStyles}
                    {...register('phone', {
                      minLength: {
                        value: phoneNumberLength,
                        message: `Phone number must be ${phoneNumberLength} digits`,
                      },
                      maxLength: {
                        value: phoneNumberLength,
                        message: `Phone number must be ${phoneNumberLength} digits`,
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Only numbers are allowed',
                      },
                    })}
                  />
                </InputGroup>
                <FormErrorMessage>
                  <>{errors.phone && errors.phone.message}</>
                </FormErrorMessage>
              </FormControl>
            </>
          )}

          <FormControl isRequired isInvalid={!!errors.password}>
            <FormLabel htmlFor="password" {...labelStyles}>
              Password
            </FormLabel>
            <PasswordInput
              inputStyles={inputStyles}
              register={register}
              regName="password"
              formType={formType}
            />
            <FormErrorMessage>
              <>{errors.password && errors.password.message}</>
            </FormErrorMessage>
          </FormControl>
          {formType === 'signup' && (
            <FormControl isRequired isInvalid={!!errors.confirmPassword}>
              <FormLabel htmlFor="confirmPassword" {...labelStyles}>
                Confirm Password
              </FormLabel>
              <PasswordInput
                inputStyles={inputStyles}
                register={register}
                regName="confirmPassword"
                formType={formType}
              />
              <FormErrorMessage>
                <>{errors.confirmPassword && errors.confirmPassword.message}</>
              </FormErrorMessage>
            </FormControl>
          )}
          {formType === 'login' && (
            <Box mt={[1]}>
              <Checkbox
                name="1"
                colorScheme="green"
                size={{ base: 'sm', md: 'md' }}
              >
                Remember Password
              </Checkbox>
            </Box>
          )}
          {formType === 'login' && (
            <Box mt={[2]} mb={[2]}>
              <Link
                onClick={handlePasswordFlow}
                size="xs"
                fontSize={{
                  base: 'sm',
                  md: 'md',
                }}
                textDecoration={'underline'}
              >
                Forgot password?
              </Link>
            </Box>
          )}
          {errors.root && (
            <Text
              color="red"
              fontSize={{
                base: 'sm',
                md: 'md',
              }}
            >
              {errors.root.message}
            </Text>
          )}
          <Button
            type="submit"
            className="font-regular mt-3 w-full rounded-lg tracking-wide text-white"
            isLoading={
              loginMutation.isPending ||
              signupMutation.isPending ||
              isFetchingSignupToken
            }
          >
            {formType === 'signup' ? 'Proceed' : 'Login'}
          </Button>
          {formType === 'login' ? (
            <Text
              mt={[3]}
              fontSize={{
                base: 'md',
                md: 'md',
              }}
              fontWeight={'regular'}
            >
              Don&apos;t have an account?
              <NextLink
                href={
                  isTv
                    ? `/auth/tvsignup?user=business${queryCode ? `&code=${queryCode}` : ''}`
                    : `/auth/signup?user=business`
                }
                className="pl-1 font-medium text-green underline"
              >
                Sign up
              </NextLink>
            </Text>
          ) : (
            <Text
              mt={[3]}
              fontSize={{
                base: 'md',
                md: 'md',
              }}
              fontWeight={'regular'}
            >
              Already have an account?{' '}
              <NextLink
                href={
                  isTv
                    ? `/auth/tvlogin?user=business${queryCode ? `&code=${queryCode}` : ''}`
                    : `/auth/login?user=business`
                }
                className="pl-1 font-medium text-green underline"
              >
                Login
              </NextLink>
            </Text>
          )}
        </VStack>
      </form>
    </>
  )
}

export default BusinessAuthForm
